import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/yanre/Reveal";
import { track } from "@/lib/track";
import { useLocale } from "@/i18n/LocaleContext";

// SECTION — Two Ways to Start. No prices: "Get your project quote".
// Composition stays customizable to space, methodology and investment level.
const PACKAGES = [
  {
    value: "starter",
    nameKey: "pts.pkg.starter.name",
    nameDefault: "Starter",
    posKey: "pts.pkg.starter.pos",
    posDefault: "Everything you need to start a professional private training studio.",
    ideal: [
      ["pts.pkg.starter.i0", "First studio"],
      ["pts.pkg.starter.i1", "Personal trainers"],
      ["pts.pkg.starter.i2", "Small spaces"],
      ["pts.pkg.starter.i3", "1-to-3 Training"],
      ["pts.pkg.starter.i4", "Semi-private training"],
    ],
    conceptKey: "pts.pkg.starter.concept",
    conceptDefault: "A carefully selected combination of essential strength, functional and free-weight equipment.",
    ctaKey: "pts.pkg.starter.cta",
    ctaDefault: "Build My Starter Studio",
    featured: false,
  },
  {
    value: "studio-pro",
    nameKey: "pts.pkg.studiopro.name",
    nameDefault: "Studio Pro",
    posKey: "pts.pkg.studiopro.pos",
    posDefault: "A complete professional setup for a premium personal training business.",
    ideal: [
      ["pts.pkg.studiopro.i0", "Established personal trainers"],
      ["pts.pkg.studiopro.i1", "Premium studios"],
      ["pts.pkg.studiopro.i2", "Higher client capacity"],
      ["pts.pkg.studiopro.i3", "Small-group training"],
      ["pts.pkg.studiopro.i4", "Performance training"],
    ],
    conceptKey: "pts.pkg.studiopro.concept",
    conceptDefault: "A more complete combination of strength, functional, free-weight and cardio equipment.",
    ctaKey: "pts.pkg.studiopro.cta",
    ctaDefault: "Build My Studio Pro",
    featured: true,
  },
];

export const PTPackages = ({ onSelectPackage }) => {
  const { t } = useLocale();
  return (
    <section className="section-dark py-24 lg:py-32" data-testid="pt-packages-section">
      <div className="container-edge">
        <Reveal>
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-10 bg-signal" />
            <span className="overline">{t("pts.pkg.label", "Two Ways to Start")}</span>
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="max-w-4xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-6xl">
            {t("pts.pkg.h2a", "Start smart.")}{" "}
            <span className="text-signal">{t("pts.pkg.h2b", "Grow on your terms.")}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-titanium/75 sm:text-lg">
            {t(
              "pts.pkg.intro",
              "A professional studio starts with a carefully selected equipment package — and grows over time. Every composition is customized to your space, training methodology and investment level."
            )}
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {PACKAGES.map((pkg, i) => (
            <Reveal key={pkg.value} delay={0.08 * i}>
              <article
                className={`flex h-full flex-col border p-8 sm:p-10 ${pkg.featured ? "border-signal bg-carbon-900" : "border-carbon-600 bg-carbon-900/60"}`}
                data-testid={`pt-package-${pkg.value}`}
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">
                  {t("pts.pkg.tag", "Package")} 0{i + 1}
                </p>
                <h3 className="mt-3 font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
                  {t(pkg.nameKey, pkg.nameDefault)}
                </h3>
                <p className="mt-4 text-base font-light leading-relaxed text-titanium/80">{t(pkg.posKey, pkg.posDefault)}</p>

                <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/40">{t("pts.pkg.idealFor", "Ideal for")}</p>
                <ul className="mt-3 space-y-2">
                  {pkg.ideal.map(([k, d]) => (
                    <li key={k} className="flex items-center gap-3 text-sm font-light text-titanium/75">
                      <Check className="h-3.5 w-3.5 shrink-0 text-signal" />
                      {t(k, d)}
                    </li>
                  ))}
                </ul>

                <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/40">{t("pts.pkg.conceptLabel", "Equipment concept")}</p>
                <p className="mt-3 text-sm font-light leading-relaxed text-titanium/70">{t(pkg.conceptKey, pkg.conceptDefault)}</p>

                <p className="mt-8 border-t border-carbon-700 pt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-titanium/60" data-testid={`pt-package-${pkg.value}-quote`}>
                  {t("pts.pkg.quote", "Get your project quote")}
                </p>
                <button
                  type="button"
                  onClick={() => onSelectPackage(pkg.value)}
                  className={`${pkg.featured ? "btn-signal" : "btn-ghost border-titanium/30 text-titanium hover:border-signal hover:text-white"} group mt-6 w-full justify-center`}
                  data-testid={`pt-package-${pkg.value}-cta`}
                >
                  {t(pkg.ctaKey, pkg.ctaDefault)}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <p className="mt-8 max-w-3xl font-mono text-[11px] uppercase tracking-[0.18em] leading-relaxed text-titanium/45" data-testid="pt-packages-note">
            {t("pts.pkg.note", "No fixed prices online — every studio is quoted as a project, composed to your space, methodology and investment level.")}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
